
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { UserRole } from "@/lib/supabase";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { Permission, hasPermission } from "@/utils/permissions";
import { useEffect, useState } from "react";

interface RequireAuthProps {
  allowedRoles?: UserRole[];
  requiredPermissions?: Permission[];
  requireAnyPermission?: boolean;
}

const RequireAuth = ({ 
  allowedRoles, 
  requiredPermissions, 
  requireAnyPermission = false 
}: RequireAuthProps) => {
  const { user, profile, isLoading, isConfigured, authError } = useAuth();
  const location = useLocation();
  const [loadingTimeout, setLoadingTimeout] = useState(false);
  
  // Set a timeout to prevent infinite loading state
  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => {
        setLoadingTimeout(true);
      }, 10000); // 10 seconds timeout
      
      return () => clearTimeout(timer);
    } else {
      setLoadingTimeout(false);
    }
  }, [isLoading]);

  // If we're still loading but hit the timeout, offer a reload option
  if (isLoading && loadingTimeout) {
    return (
      <div className="flex flex-col h-screen items-center justify-center p-4">
        <Alert variant="destructive" className="max-w-md mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Authentication Taking Too Long</AlertTitle>
          <AlertDescription>
            The authentication process is taking longer than expected. This could be due to network issues or service disruptions.
          </AlertDescription>
        </Alert>
        <div className="flex space-x-4 mt-4">
          <Button onClick={() => window.location.reload()} variant="default">
            Reload Page
          </Button>
          <Button asChild variant="outline">
            <Link to="/login">Return to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-screen items-center justify-center p-4">
        <div className="w-full max-w-md space-y-4">
          <Skeleton className="h-8 w-3/4 mx-auto" />
          <Skeleton className="h-4 w-1/2 mx-auto" />
          <div className="space-y-2">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
        <p className="text-gray-500 dark:text-gray-400 mt-4">Verifying your credentials...</p>
      </div>
    );
  }

  if (!isConfigured) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4">
        <Alert variant="destructive" className="max-w-md mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Authentication Not Configured</AlertTitle>
          <AlertDescription>
            Supabase environment variables are missing or incorrectly set. Please check your Supabase connection.
          </AlertDescription>
        </Alert>
        <div className="text-center">
          <p className="mb-4">To enable authentication, follow these steps:</p>
          <ol className="text-left list-decimal pl-5 space-y-2 mb-6">
            <li>Click the green Supabase button in the top right corner</li>
            <li>Select "Connect to Supabase"</li>
            <li>Complete the connection process</li>
            <li>Refresh the page after connection is complete</li>
          </ol>
          <div className="space-x-4">
            <Button asChild variant="secondary">
              <Link to="/">Go to Home</Link>
            </Button>
            <Button asChild>
              <a href="https://docs.lovable.dev/integrations/supabase/" target="_blank" rel="noopener noreferrer">
                Supabase Integration Docs
              </a>
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (authError) {
    return (
      <div className="flex flex-col items-center justify-center h-screen p-4">
        <Alert variant="destructive" className="max-w-md mb-4">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>Authentication Error</AlertTitle>
          <AlertDescription>
            {authError}
          </AlertDescription>
        </Alert>
        <div className="space-x-4 mt-4">
          <Button onClick={() => window.location.reload()} variant="default">
            Reload Page
          </Button>
          <Button asChild variant="secondary">
            <Link to="/">Go to Home</Link>
          </Button>
          <Button asChild variant="outline">
            <Link to="/login">Return to Login</Link>
          </Button>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!profile) {
    return (
      <div className="flex h-screen items-center justify-center">
        <div className="text-center max-w-md p-4">
          <Alert variant="default" className="mb-4">
            <AlertTriangle className="h-4 w-4" />
            <AlertTitle>Profile Not Found</AlertTitle>
            <AlertDescription>
              We couldn't retrieve your profile information. This could be temporary.
            </AlertDescription>
          </Alert>
          <Button onClick={() => window.location.reload()} className="mt-4">
            Retry
          </Button>
        </div>
      </div>
    );
  }

  if (allowedRoles && !allowedRoles.includes(profile.role)) {
    return <Navigate to="/unauthorized" state={{ from: location, reason: "You don't have the required role to access this page" }} replace />;
  }

  if (requiredPermissions && requiredPermissions.length > 0) {
    const hasRequiredPermissions = requireAnyPermission
      ? requiredPermissions.some(permission => hasPermission(profile.role, permission))
      : requiredPermissions.every(permission => hasPermission(profile.role, permission));

    if (!hasRequiredPermissions) {
      return <Navigate to="/unauthorized" state={{ from: location, reason: "You don't have the required permissions to access this page" }} replace />;
    }
  }

  return <Outlet />;
};

export default RequireAuth;
